import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { formatLocalDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Bell, Send, Info, Calendar as CalendarIcon } from "lucide-react";

export default function AnnouncementsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSubmit, setOpenSubmit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/announcements");
      setItems(data);
    } catch {
      toast.error("Failed to load announcements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return items
      .filter((a) => a.event_date && a.event_date >= today)
      .sort((a, b) => a.event_date.localeCompare(b.event_date))
      .slice(0, 3);
  }, [items]);

  return (
    <div data-testid="announcements-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Community</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421]">Announcements &amp; Events</h1>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EADCC8] text-[#2C4C3B] cursor-help" data-testid="submit-info-tooltip">
                  <Info className="w-4 h-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="font-medium mb-1">How submissions work</p>
                <p className="text-xs">Homeowners can submit community announcements or event notices. The HOA Board reviews each submission — you'll get an email once it's approved and published.</p>
              </TooltipContent>
            </Tooltip>
            <Dialog open={openSubmit} onOpenChange={setOpenSubmit}>
              <DialogTrigger asChild>
                <Button data-testid="submit-ann-btn" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
                  <Send className="w-4 h-4 mr-2" /> Submit an Announcement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <ResidentSubmitForm onDone={() => { setOpenSubmit(false); load(); }} />
              </DialogContent>
            </Dialog>
          </div>
        </TooltipProvider>
      </div>

      {upcoming.length > 0 && (
        <Card className="rounded-2xl border-[#EADCC8] bg-[#FDF9F1] mb-8" data-testid="upcoming-events-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="w-5 h-5 text-[#C86650]" />
              <p className="text-xs uppercase tracking-[0.2em] text-[#C86650] font-medium">Upcoming Events</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {upcoming.map((e) => (
                <div key={e.id} className="rounded-xl bg-white p-4 border border-[#EADCC8]" data-testid={`upcoming-${e.id}`}>
                  <p className="text-xs text-[#C86650] font-medium">{formatLocalDate(e.event_date, { weekday: "short", month: "short", day: "numeric" })}</p>
                  <p className="font-serif text-lg text-[#1A2421] mt-1 truncate">{e.title}</p>
                  <p className="text-xs text-[#5C6A64] mt-1 line-clamp-2">{e.body}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-[#5C6A64]">Loading announcements…</div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-12 text-center text-[#5C6A64]">No announcements yet.</CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((a) => (
            <Card key={a.id} data-testid={`announcement-${a.id}`} className="rounded-2xl border-[#D5CFC4] hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-shadow">
              <CardHeader className="flex flex-row items-start gap-4">
                <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 bg-[#EADCC8]">
                  <Bell className="w-5 h-5 text-[#2C4C3B]" />
                </div>
                <div>
                  <CardTitle className="font-serif text-2xl text-[#1A2421]">{a.title}</CardTitle>
                  <div className="flex gap-2 mt-2 items-center flex-wrap">
                    <Badge className="bg-[#2C4C3B] text-white hover:bg-[#233D2F]">{a.category}</Badge>
                    {a.event_date && <Badge variant="outline" className="border-[#2C4C3B]">📅 {formatLocalDate(a.event_date)}</Badge>}
                    <span className="text-xs text-[#5C6A64]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""} · {a.author || a.contact_name}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#1A2421] leading-relaxed whitespace-pre-line">{a.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ResidentSubmitForm({ onDone }) {
  const [form, setForm] = useState({ title: "", body: "", event_date: "", contact_name: "", contact_email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title || !form.body || !form.contact_name || !form.contact_email) return toast.error("All required fields must be filled");
    setBusy(true);
    try {
      await api.post("/announcements/submit", form);
      toast.success("Submitted! The board will review shortly.");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Submit an Announcement</DialogTitle></DialogHeader>
      <div className="bg-[#FDF3F0] border border-[#C86650] rounded-xl p-4 text-sm text-[#1A2421]">
        <strong className="text-[#C86650]">Board Approval Required:</strong> All community announcements are reviewed by the HOA Board before being published to ensure information is appropriate and beneficial to the community.
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Title *</label>
        <Input data-testid="submit-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Description *</label>
        <Textarea data-testid="submit-body" required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1 rounded-xl" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Event date (optional)</label>
        <Input data-testid="submit-event-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-1 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Your name *</label>
          <Input data-testid="submit-contact-name" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 rounded-xl" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Your email *</label>
          <Input data-testid="submit-contact-email" type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1 rounded-xl" />
        </div>
      </div>
      <Button data-testid="submit-ann-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white">
        {busy ? "Submitting…" : "Submit for board review"}
      </Button>
    </div>
  );
}
